import React from 'react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {Form, FormField, FormInput} from '@/components/ui/form'
import {Button} from '@/components/ui/button'
import {zodResolver} from '@hookform/resolvers/zod'
import {useForm} from 'react-hook-form'
import {z} from 'zod'

import {useShopperLoginMutation} from '@salesforce/commerce-sdk-react'

interface ForgotPasswordFormProps {
    onSuccess?: () => void
    onEmailTypeChange?: (type: 'reset-password' | 'passwordless') => void
}

type PasswordlessRequest = {
    user_id: string
    mode: string
    channel_id: string
    callback_uri: string
}

type ResetPasswordRequest = {
    user_id: string
    mode: string
    channel_id: string
    callback_uri: string
    hint: string
}

const ForgotPasswordForm = ({onSuccess, onEmailTypeChange}: ForgotPasswordFormProps) => {
    const {site} = useMultiSite()
    const siteId: string = site.id

    const FormSchema = z.object({
        email: z
            .string({
                required_error: 'This field is required'
            })
            .min(1, 'This field is required')
            .email({message: 'Please enter a valid email address'})
    })

    const form = useForm<{email: string}>({
        resolver: zodResolver(FormSchema),
        defaultValues: {
            email: ''
        }
    })

    const getResetPasswordTokenLogin = useShopperLoginMutation('getPasswordResetToken')
    const authorizePasswordlessCustomer = useShopperLoginMutation('authorizePasswordlessCustomer')

    const setGenericEmailError = (flow: 'passwordless' | 'reset-password', error: unknown) => {
        console.error(`[${flow}]`, error)
        form.setError('email', {
            type: 'manual',
            message: 'Something went wrong, please try again later.'
        })
    }

    const handlePasswordlessLogin = async ({email}: {email: string}) => {
        form.clearErrors('email')
        onEmailTypeChange?.('passwordless')

        const body: PasswordlessRequest = {
            user_id: email,
            mode: 'callback',
            channel_id: siteId,
            callback_uri: `${window.location.origin}/api/passwordless/callback`
        }

        try {
            await authorizePasswordlessCustomer.mutateAsync({body})
            onSuccess?.()
        } catch (error) {
            setGenericEmailError('passwordless', error)
        }
    }

    const handleResetPassword = async ({email}: {email: string}) => {
        form.clearErrors('email')
        onEmailTypeChange?.('reset-password')

        const body: ResetPasswordRequest = {
            user_id: email,
            mode: 'callback',
            channel_id: siteId,
            hint: 'cross_device',
            callback_uri: `${window.location.origin}/api/password-reset`
        }

        try {
            await getResetPasswordTokenLogin.mutateAsync({body})
            onSuccess?.()
        } catch (error) {
            setGenericEmailError('reset-password', error)
        }
    }

    return (
        <Form {...form} data-testid="forgotPasswordForm">
            <form onSubmit={form.handleSubmit(handleResetPassword)} className="space-y-6">
                <FormField
                    control={form.control}
                    name="email"
                    render={({field}) => (
                        <FormInput
                            label="Email"
                            inputType="email"
                            required
                            fieldProps={field}
                            validate={() => form.trigger('email')}
                            data-testid="forgotPasswordForm-FormInput"
                        />
                    )}
                    data-testid="forgotPasswordForm-FormField"
                />

                <div className="flex flex-col gap-2">
                    <Button
                        type="button"
                        ctaType="button"
                        variant="primary"
                        className="w-[18.75rem] mx-auto"
                        data-testid="forgotPasswordForm-PasswordlessLoginButton"
                        onClick={form.handleSubmit(handlePasswordlessLogin)}
                    >
                        Email Sign In Link
                    </Button>

                    <Button
                        type="submit"
                        ctaType="button"
                        variant="primary"
                        className="w-[18.75rem] mx-auto"
                        data-testid="forgotPasswordForm-Button"
                    >
                        Reset Password
                    </Button>
                </div>
            </form>
        </Form>
    )
}

export default ForgotPasswordForm
